import { create } from 'zustand';
import type { ViewMode } from './playback';
import type { FilmRoomSpatialSyncInput } from '@/lib/film-room-spatial-sync';

export type CameraTarget =
  | { type: 'wholeCourse' }
  | { type: 'boat'; boatId: string }
  | { type: 'leader' }
  | { type: 'group'; boatIds: string[] }
  | { type: 'feature'; featureId: string };

export type ViewerActivePhase = 'unknown' | 'prestart' | 'racing' | 'finished';
export type ViewerDetailMode = 'live' | 'stats' | 'maneuvers' | 'course' | 'fleet' | 'video';
/** v2 mobile shell: Map · 60/40 · Data split spectrum (#1019, #1041) */
export type MobileSplit = 'map' | '6040' | 'data';
export type DesktopSplit = 'map' | 'split' | 'data';

interface FilmRoomSpatialSnapshot {
  cameraTarget: CameraTarget;
  focusBoatIds: string[];
}

export interface FilmRoomSpatialSession extends FilmRoomSpatialSyncInput {
  snapshot: FilmRoomSpatialSnapshot;
}

interface ViewerIaState {
  raceId: string | null;
  focusBoatIds: string[];
  /** Focus LOCK (Jason memo #234): while locked, boat/row clicks cannot
   *  change the focus selection — guards against accidental unselects. */
  focusLocked: boolean;
  activeBoatId: string | null;
  cameraTarget: CameraTarget;
  /** Sphere lens tracking (#1484): the OTHER boat the 360 view auto-tracks
   *  from the camera boat. Orthogonal to focus and to cameraTarget — which
   *  boat you ride, which boats you watch, and which boat the lens follows
   *  are three independent choices (epic #1478). The camera boat is never a
   *  valid target: useSphereFollow clears it and resets the look to the bow
   *  (the store cannot know the camera boat — that identity is per clip). */
  lensTarget: { boatId: string } | null;
  /** One Film Room spatial session owns one original viewer snapshot across
   *  clip changes. Overlay arrival may enrich it but never re-snapshot it. */
  filmRoomSpatialSession: FilmRoomSpatialSession | null;
  viewerMode: ViewMode;
  detailMode: ViewerDetailMode;
  activePhase: ViewerActivePhase;
  mobileSplit: MobileSplit;
  /** Map fraction of the split view (0..1). Draggable via the seam handle. */
  mobileSplitRatio: number;
  /** Desktop map/data arrangement: all-map · split · all-data (Jason memo D1). */
  desktopSplit: DesktopSplit;
  /** Width (px) of the desktop detail rail in split view. Draggable divider. */
  desktopDetailWidth: number;
  /** On-map option pickers (Trails/Focus/Follow) visibility — the D2 gear.
   *  Default open on desktop per Jason's memo. */
  showMapPickers: boolean;
  /** Fleet-rail search/Focus tools visibility — the D3 magnifier. */
  showFleetTools: boolean;
  /** In-video fleet drawer (#1481) open state. Moved out of VideoOverlay's
   *  local state (Julian 2026-08-10): the toggle button now rides the mini
   *  map's ⚙ cluster (MapInsetControls), which renders OUTSIDE the overlay
   *  tree — a shared store is the only owner both surfaces can reach.
   *  Ephemeral by design; never persisted. */
  videoFleetOpen: boolean;
  /** Left Focus/fleet rail collapsed to a thin rail (Jason memo #246) —
   *  with the right panel on all-map, the map takes the full width. */
  fleetCollapsed: boolean;
  /** Follow-framing tightness (Jason, Zoom 4:50 / local-batch-1 f4): a
   *  multiplier on the follow camera's fit padding. 1 = default framing,
   *  <1 tighter (less water around the boats), >1 looser. Adjusted by the
   *  +/- keys while a moving follow target is active; survives target
   *  changes so a chosen framing sticks for the session. */
  followTightness: number;

  setRaceScope: (raceId: string | null) => void;
  setFocusBoatIds: (ids: string[]) => void;
  focusBoat: (boatId: string) => void;
  unfocusBoat: (boatId: string) => void;
  clearFocus: () => void;
  toggleFocusLocked: () => void;
  toggleFleetCollapsed: () => void;
  setActiveBoatId: (boatId: string | null) => void;
  setCameraTarget: (target: CameraTarget) => void;
  setLensTarget: (target: { boatId: string } | null) => void;
  /** Step the follow framing one notch tighter (-1) or looser (+1) on the
   *  clamped multiplicative ladder. Returns the new tightness for HUD copy. */
  adjustFollowTightness: (direction: 1 | -1) => number;
  beginFilmRoomSpatialSync: (input: FilmRoomSpatialSyncInput) => void;
  endFilmRoomSpatialSync: () => void;
  setViewerMode: (mode: ViewMode) => void;
  setDetailMode: (mode: ViewerDetailMode) => void;
  setMobileSplit: (split: MobileSplit) => void;
  setMobileSplitRatio: (ratio: number) => void;
  setDesktopSplit: (split: DesktopSplit) => void;
  setDesktopDetailWidth: (width: number) => void;
  toggleMapPickers: () => void;
  toggleFleetTools: () => void;
  setVideoFleetOpen: (open: boolean) => void;
  toggleVideoFleet: () => void;
  syncActivePhase: (currentTime: string, raceStartTime?: string, raceEndTime?: string) => void;
  reset: () => void;
}

const DEFAULT_CAMERA_TARGET: CameraTarget = { type: 'wholeCourse' };

/** Follow-framing tightness bounds and step (local-batch-1 f4). One step is
 *  a quarter more/less padding; the range spans "right on top of the boats"
 *  to "plenty of context" without ever starving cameraForBounds. */
export const FOLLOW_TIGHTNESS_DEFAULT = 1;
export const FOLLOW_TIGHTNESS_MIN = 0.5;
export const FOLLOW_TIGHTNESS_MAX = 2.5;
export const FOLLOW_TIGHTNESS_STEP = 1.25;

function clampFollowTightness(value: number): number {
  const clamped = Math.min(
    FOLLOW_TIGHTNESS_MAX,
    Math.max(FOLLOW_TIGHTNESS_MIN, value),
  );
  // 3 decimals keeps repeated x/÷1.25 steps reversible without float crumbs.
  return Math.round(clamped * 1000) / 1000;
}

function cloneCameraTarget(target: CameraTarget): CameraTarget {
  return target.type === 'group'
    ? { type: 'group', boatIds: [...target.boatIds] }
    : { ...target };
}

function restoredSpatialPatch(session: FilmRoomSpatialSession | null) {
  if (!session) return {};
  return {
    cameraTarget: cloneCameraTarget(session.snapshot.cameraTarget),
    focusBoatIds: [...session.snapshot.focusBoatIds],
    filmRoomSpatialSession: null,
  };
}

/** Default map fraction of the mobile split view (the historical 60/40). */
export const DEFAULT_MOBILE_SPLIT_RATIO = 0.6;
export const MOBILE_SPLIT_RATIO_MIN = 0.25;
export const MOBILE_SPLIT_RATIO_MAX = 0.85;

/** Desktop detail-rail width bounds (px). Default is the historical 416. */
export const DEFAULT_DESKTOP_DETAIL_WIDTH = 416;
export const DESKTOP_DETAIL_WIDTH_MIN = 300;
export const DESKTOP_DETAIL_WIDTH_MAX = 800;

function uniqueBoatIds(ids: string[]): string[] {
  // No hard cap (Jason 2026-07-04): the All-boats preset focuses the whole
  // fleet. MAX_FOCUSED_BOATS still sizes the curated presets (leaders etc.).
  const seen = new Set<string>();
  const next: string[] = [];
  for (const id of ids) {
    if (!id || seen.has(id)) continue;
    seen.add(id);
    next.push(id);
  }
  return next;
}

export function resolveViewerActivePhase(
  currentTime: string,
  raceStartTime?: string,
  raceEndTime?: string,
): ViewerActivePhase {
  const currentMs = Date.parse(currentTime);
  if (!Number.isFinite(currentMs)) return 'unknown';

  const startMs = raceStartTime ? Date.parse(raceStartTime) : Number.NaN;
  const endMs = raceEndTime ? Date.parse(raceEndTime) : Number.NaN;

  if (Number.isFinite(startMs) && currentMs < startMs) return 'prestart';
  if (Number.isFinite(endMs) && currentMs >= endMs) return 'finished';
  if (Number.isFinite(startMs) || Number.isFinite(endMs)) return 'racing';
  return 'unknown';
}

export const useViewerIaStore = create<ViewerIaState>()((set, get) => ({
  raceId: null,
  focusBoatIds: [],
  focusLocked: false,
  activeBoatId: null,
  cameraTarget: DEFAULT_CAMERA_TARGET,
  lensTarget: null,
  filmRoomSpatialSession: null,
  viewerMode: 'analytical',
  detailMode: 'live',
  activePhase: 'unknown',
  mobileSplit: 'map',
  mobileSplitRatio: DEFAULT_MOBILE_SPLIT_RATIO,
  desktopSplit: 'split',
  desktopDetailWidth: DEFAULT_DESKTOP_DETAIL_WIDTH,
  showMapPickers: true,
  showFleetTools: true,
  fleetCollapsed: false,
  videoFleetOpen: false,
  followTightness: FOLLOW_TIGHTNESS_DEFAULT,

  setRaceScope: (raceId) => {
    const current = get().raceId;
    if (current === raceId) return;
    set({
      raceId,
      focusBoatIds: [],
      // A new race starts with an empty focus set — a carried-over lock
      // would freeze it empty and read as "clicking does nothing".
      focusLocked: false,
      activeBoatId: null,
      cameraTarget: DEFAULT_CAMERA_TARGET,
      // A lens target is a boat in THIS race — like focus, it cannot mean
      // anything in the next one. But the FIRST scoping (null ⇒ a race) is
      // not a race change: it happens after the viewer has already applied
      // the arriving URL, so clearing there would silently eat every
      // `follow=` deep link (#1484). `boat=`/`focus=` survive this only
      // because they have a second, race-gated application path; the lens
      // target has none, so it is preserved instead. With no previous race
      // there is nothing stale to clear.
      ...(current === null ? {} : { lensTarget: null }),
      filmRoomSpatialSession: null,
      detailMode: 'live',
      activePhase: 'unknown',
      mobileSplit: 'map',
      // The in-video drawer shows THIS race's fleet — it cannot mean
      // anything in the next one.
      videoFleetOpen: false,
    });
  },

  setFocusBoatIds: (ids) => {
    set({ focusBoatIds: uniqueBoatIds(ids) });
  },

  focusBoat: (boatId) => {
    const { focusBoatIds } = get();
    if (focusBoatIds.includes(boatId)) return;
    set({ focusBoatIds: uniqueBoatIds([...focusBoatIds, boatId]) });
  },

  unfocusBoat: (boatId) => {
    set((state) => ({
      focusBoatIds: state.focusBoatIds.filter((id) => id !== boatId),
    }));
  },

  clearFocus: () => {
    set({ focusBoatIds: [] });
  },

  toggleFocusLocked: () => {
    set((state) => ({ focusLocked: !state.focusLocked }));
  },

  toggleFleetCollapsed: () => {
    set((state) => ({ fleetCollapsed: !state.fleetCollapsed }));
  },

  setActiveBoatId: (boatId) => {
    set({ activeBoatId: boatId });
  },

  setCameraTarget: (target) => {
    set({ cameraTarget: target });
  },

  setLensTarget: (target) => {
    set({ lensTarget: target });
  },

  adjustFollowTightness: (direction) => {
    const next = clampFollowTightness(
      direction > 0
        ? get().followTightness * FOLLOW_TIGHTNESS_STEP
        : get().followTightness / FOLLOW_TIGHTNESS_STEP,
    );
    set({ followTightness: next });
    return next;
  },

  beginFilmRoomSpatialSync: (input) => {
    const involvedBoatIds = uniqueBoatIds(input.involvedBoatIds).slice(0, 4);
    if (involvedBoatIds.length === 0) return;

    set((state) => {
      const current = state.filmRoomSpatialSession;
      const normalizedInput = {
        ...input,
        involvedBoatIds,
        cameraBoatId: input.cameraBoatId || null,
      };

      // Overlay arrival and ordinary re-renders only enrich the active clip.
      // They must not re-snapshot or reapply focus over a user's interaction.
      if (current?.clipId === input.clipId) {
        if (
          current.cameraBoatId === normalizedInput.cameraBoatId
          && current.trailTimeRange?.start === normalizedInput.trailTimeRange?.start
          && current.trailTimeRange?.end === normalizedInput.trailTimeRange?.end
        ) return state;
        return {
          filmRoomSpatialSession: {
            ...current,
            cameraBoatId: normalizedInput.cameraBoatId,
            trailTimeRange: normalizedInput.trailTimeRange,
          },
        };
      }

      const snapshot = current?.snapshot ?? {
        cameraTarget: cloneCameraTarget(state.cameraTarget),
        focusBoatIds: [...state.focusBoatIds],
      };
      return {
        cameraTarget: { type: 'group', boatIds: involvedBoatIds },
        focusBoatIds: state.focusLocked ? state.focusBoatIds : involvedBoatIds,
        filmRoomSpatialSession: {
          ...normalizedInput,
          snapshot,
        },
      };
    });
  },

  endFilmRoomSpatialSync: () => {
    set((state) => {
      if (!state.filmRoomSpatialSession) return state;
      return restoredSpatialPatch(state.filmRoomSpatialSession);
    });
  },

  setViewerMode: (mode) => {
    set({ viewerMode: mode });
  },

  setDetailMode: (mode) => {
    set((state) => ({
      detailMode: mode,
      ...(state.detailMode === 'video' && mode !== 'video'
        ? restoredSpatialPatch(state.filmRoomSpatialSession)
        : {}),
    }));
  },

  setMobileSplit: (split) => {
    // Jason 2026-07-06 (reverses #1019): the split spectrum arranges PANES,
    // it does not change the view — Stats + split shows Stats beside the
    // map. The ☰ menu is how views change.
    set({ mobileSplit: split });
  },

  setMobileSplitRatio: (ratio) => {
    // Clamp so neither pane can be dragged away entirely — the split spectrum
    // (Map / Data buttons) is how you go single-pane, not the drag handle.
    // Rounded to 3 decimals to keep flex values clean of float artifacts.
    const clamped = Math.min(
      MOBILE_SPLIT_RATIO_MAX,
      Math.max(MOBILE_SPLIT_RATIO_MIN, ratio),
    );
    set({ mobileSplitRatio: Math.round(clamped * 1000) / 1000 });
  },

  setDesktopSplit: (split) => {
    set({ desktopSplit: split });
  },

  toggleMapPickers: () => {
    set((state) => ({ showMapPickers: !state.showMapPickers }));
  },

  toggleFleetTools: () => {
    set((state) => ({ showFleetTools: !state.showFleetTools }));
  },

  setVideoFleetOpen: (open) => {
    set({ videoFleetOpen: open });
  },

  toggleVideoFleet: () => {
    set((state) => ({ videoFleetOpen: !state.videoFleetOpen }));
  },

  setDesktopDetailWidth: (width) => {
    // Same philosophy as mobile: the divider adjusts the split, the spectrum
    // buttons are how you go single-pane.
    set({
      desktopDetailWidth: Math.round(
        Math.min(DESKTOP_DETAIL_WIDTH_MAX, Math.max(DESKTOP_DETAIL_WIDTH_MIN, width)),
      ),
    });
  },

  syncActivePhase: (currentTime, raceStartTime, raceEndTime) => {
    const next = resolveViewerActivePhase(currentTime, raceStartTime, raceEndTime);
    if (get().activePhase !== next) {
      set({ activePhase: next });
    }
  },

  reset: () => {
    set({
      raceId: null,
      focusBoatIds: [],
      focusLocked: false,
      activeBoatId: null,
      cameraTarget: DEFAULT_CAMERA_TARGET,
      lensTarget: null,
      filmRoomSpatialSession: null,
      viewerMode: 'analytical',
      detailMode: 'live',
      activePhase: 'unknown',
      mobileSplit: 'map',
      videoFleetOpen: false,
    });
  },
}));
